import { trpc } from "@client/trpc";
import { isRichTextBlank } from "@lib/isRichTextBlank";
import { useEffect, useRef } from "react";

/** Blank markup ⇔ "" so clearing `<p><br></p>` doesn't count as a change. */
export function normalizeGeneralNotesForRefresh(
  generalNotes: string | undefined,
): string {
  return isRichTextBlank(generalNotes ?? "") ? "" : (generalNotes ?? "");
}

/**
 * A fingerprint of every member's round-level general note, for change
 * detection only (never compared against server hashes). Sorted by member id
 * so map iteration order can't flap it.
 */
function buildGeneralNotesFingerprint(
  members: Record<string, { generalNotes?: string } | undefined> | undefined,
): string {
  if (members == null) return "";
  return Object.entries(members)
    .map(
      ([memberId, member]) =>
        `${memberId}${normalizeGeneralNotesForRefresh(member?.generalNotes)}`,
    )
    .sort()
    .join(" ");
}

/**
 * Round-shell-wide sync, called from RoundShellEffects (the shell's one
 * per-emission seam — no extra store subscription): refetches MVF consistency
 * ratings when ANY member's round-level general note changes.
 *
 * The general note feeds the rating prompts server-side without being part of
 * the client-computed evidence hash, so an edit leaves local hash comparisons
 * unchanged and cached `getMvfQualityRating` responses still claim
 * `stale: false`. Round-store updates bypass the React Query cache, so the
 * invalidation must be wired explicitly — and it lives at the store seam
 * rather than in individual rating consumers: per-consumer listeners left
 * consumers without one (the Review workspace's eval modal) serving the
 * outdated flag. One note feeds EVERY proposal's rating for its member, so
 * the invalidation is procedure-wide; edits are rare and only
 * actively-observed queries refetch.
 *
 * Known limit: a viewer whose aggregate REDACTS the note sees a constant
 * sentinel, so no client-side change signal exists for them — their cached
 * entries refresh on the queries' normal refetch paths (mount, focus, the
 * eval modal's revalidate-on-open) and then carry the server's stale verdict.
 */
export function useMvfGeneralNotesRatingRefresh(
  members: Record<string, { generalNotes?: string } | undefined> | undefined,
): void {
  const fingerprint = buildGeneralNotesFingerprint(members);
  const utils = trpc.useUtils();

  const prevRef = useRef(fingerprint);
  useEffect(() => {
    if (prevRef.current === fingerprint) {
      return;
    }
    prevRef.current = fingerprint;
    utils.getMvfQualityRating.invalidate().catch(() => {});
  }, [fingerprint, utils]);
}
